/**
 * Lightweight zero-dep landing-page analytics client.
 *
 * - One session ID per browser tab (sessionStorage, not localStorage — so it
 *   resets per visit which keeps "session" semantics intuitive).
 * - Fires page_view on init.
 * - Fires section_view when a <section id="..."> is 50% in the viewport (once
 *   per section per session).
 * - Exposes trackClick / trackFormSubmit / trackInstallAttempt for explicit
 *   user actions.
 *
 * No cookies. No PII. Posts to our own /api/landing/track.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'gk_landing_session';

let sessionId = null;
let initialized = false;
const seenSections = new Set();
let observer = null;

function getSessionId() {
  if (sessionId) return sessionId;
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `s_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  sessionId = id;
  return id;
}

async function send(event, extra = {}) {
  try {
    await axios.post(
      `${API}/landing/track`,
      {
        event,
        session_id: getSessionId(),
        path: window.location.pathname,
        referrer: document.referrer || null,
        ...extra,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );
  } catch {
    // Silent: never break the user experience for analytics.
  }
}

export function initLandingAnalytics() {
  if (initialized) return;
  initialized = true;
  send('page_view');

  // Watch every <section id="..."> on the landing page.
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = entry.target.id;
          if (!id || seenSections.has(id)) return;
          seenSections.add(id);
          send('section_view', { section: id });
        });
      },
      { threshold: 0.5 }
    );
    document.querySelectorAll('section[id]').forEach((s) => observer.observe(s));
    // After React paints late content, re-scan once.
    setTimeout(() => {
      document.querySelectorAll('section[id]').forEach((s) => observer.observe(s));
    }, 1500);
  }
}

export function teardownLandingAnalytics() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  initialized = false;
  seenSections.clear();
}

export const trackClick = (target, section) =>
  send('click', { target, section: section || null });

export const trackFormSubmit = (target = 'lead-form') =>
  send('form_submit', { target });

export const trackInstallAttempt = (source = 'unknown') =>
  send('install_attempt', { target: source });
